/** Rekurzivní renderer stromu layoutu. container→flex, grid→css grid, tabs→Radix, widget→registr.
 *  V edit+dnd módu (desktop) jsou děti kontejnerů draggable (@dnd-kit, handle-based) a kontejnery droppable. */

import React from "react";
import { useDroppable } from "@dnd-kit/core";
import { SortableContext, horizontalListSortingStrategy, rectSortingStrategy, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import type { ContainerNode, GridNode, LayoutAlign, LayoutJustify, LayoutNode, LayoutSize, Sides } from "@globals/layouts";
import { renderWidget, type WidgetContext } from "@client/config/layoutWidgets";
import { ResizeSplitter } from "./editor/ResizeSplitter";
import { useLayoutEditor } from "@client/store/layoutEditor";
import { useIsMobile } from "@client/hooks/useIsMobile";
import { AppErrorBoundary } from "@client/components/ui/error-boundary";
import { cn } from "@client/lib/cn";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@client/components/ui/tabs";

const JUSTIFY: Record<LayoutJustify, React.CSSProperties["justifyContent"]> = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
  between: "space-between",
  around: "space-around",
  evenly: "space-evenly",
};
const ALIGN: Record<LayoutAlign, React.CSSProperties["alignItems"]> = {
  start: "flex-start",
  center: "center",
  end: "flex-end",
  stretch: "stretch",
  baseline: "baseline",
};

/** number = všechny strany, {t,r,b,l} = per-strana. 0/undefined → undefined (ať se uplatní edit fallback). */
const sidesToCss = (v: number | Sides | undefined): string | undefined => {
  if (v == null) return undefined;
  if (typeof v === "number") return v ? `${v}px` : undefined;
  return `${v.t ?? 0}px ${v.r ?? 0}px ${v.b ?? 0}px ${v.l ?? 0}px`;
};

/** grid-template-columns: responzivní (auto-fill minmax) když je minColW, jinak fixní počet sloupců. */
const gridTemplate = (node: GridNode): string =>
  node.minColW ? `repeat(auto-fill, minmax(${node.minColW}px, 1fr))` : `repeat(${node.cols}, minmax(0, 1fr))`;

const sizeStyle = (s?: LayoutSize): React.CSSProperties => ({
  flexGrow: s?.grow ?? 0,
  flexShrink: 1,
  flexBasis: s?.basis ?? "auto",
  minWidth: s?.minW ?? 0,
  minHeight: s?.minH ?? 0,
  height: typeof s?.height === "number" ? s.height : undefined,
  margin: sidesToCss(s?.margin),
  padding: sidesToCss(s?.padding),
});

/** Slot pro dítě stackovaného (mobilního) řádku: plná šířka, přirozená výška — žádný basis:50% (= výška ve sloupci!). */
const STACKED_SLOT: React.CSSProperties = { flex: "0 0 auto", width: "100%", minWidth: 0 };

const containerStyle = (node: ContainerNode, edit: boolean, stacked: boolean): React.CSSProperties => ({
  display: "flex",
  flexDirection: stacked ? "column" : node.dir === "row" ? "row" : "column",
  gap: node.gap,
  padding: sidesToCss(node.padding) ?? (edit ? "4px" : "0"),
  justifyContent: JUSTIFY[node.justify],
  alignItems: ALIGN[node.align],
  flexWrap: stacked ? "nowrap" : node.wrap ? "wrap" : "nowrap",
  minWidth: 0,
  minHeight: edit ? 24 : 0,
  height: "100%",
});

/** Obalí uzel v edit módu: klik = výběr (stopProp), ring u vybraného, label badge. */
const EditWrap: React.FC<{ id: string; kind: string; children: React.ReactNode }> = ({ id, kind, children }) => {
  const selected = useLayoutEditor((s) => s.selectedId === id);
  const select = useLayoutEditor((s) => s.select);
  return (
    <div
      className={cn("relative h-full rounded ring-inset", selected ? "ring-2 ring-primary" : "ring-1 ring-border/40 hover:ring-primary/50")}
      onClick={(e) => {
        e.stopPropagation();
        select(id);
      }}
    >
      {selected && (
        <span className="pointer-events-none absolute -top-2 left-1 z-10 rounded bg-primary px-1 text-[9px] leading-tight text-primary-foreground">{kind}</span>
      )}
      {kind === "widget" ? <div className="pointer-events-none h-full">{children}</div> : children}
    </div>
  );
};

/** Sortable slot dítěte kontejneru/mřížky (dnd mód). Drag jen za handle (⠿), ať klik = výběr.
 *  `baseStyle` = flex sizeStyle (kontejner) nebo grid-cell style (mřížka). */
const DndSlot: React.FC<{ node: LayoutNode; ctx: WidgetContext; baseStyle: React.CSSProperties }> = ({ node, ctx, baseStyle }) => {
  const { setNodeRef, attributes, listeners, transform, transition, isDragging } = useSortable({ id: node.id });
  const style: React.CSSProperties = {
    ...baseStyle,
    transform: CSS.Translate.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : undefined,
    position: "relative",
  };
  return (
    <div ref={setNodeRef} style={style}>
      <button
        type="button"
        {...attributes}
        {...listeners}
        onClick={(e) => e.stopPropagation()}
        title="Táhni pro přesun"
        className="absolute -top-1 -right-1 z-20 cursor-grab touch-none rounded bg-primary/80 px-1 text-[10px] leading-none text-primary-foreground"
      >
        ⠿
      </button>
      <NodeView node={node} ctx={ctx} edit dnd />
    </div>
  );
};

/** Kontejner v dnd módu: droppable (drop dovnitř) + SortableContext (přeřazení dětí). */
const DndContainer: React.FC<{ node: ContainerNode; ctx: WidgetContext }> = ({ node, ctx }) => {
  const { setNodeRef, isOver } = useDroppable({ id: "cont:" + node.id });
  const childIds = node.children.map((c) => c.id);
  const strategy = node.dir === "row" ? horizontalListSortingStrategy : verticalListSortingStrategy;
  return (
    <EditWrap id={node.id} kind="container">
      <div ref={setNodeRef} style={containerStyle(node, true, false)} className={cn(isOver && "bg-primary/10")}>
        <SortableContext items={childIds} strategy={strategy}>
          {node.children.map((ch, i) => (
            <React.Fragment key={ch.id}>
              <DndSlot node={ch} ctx={ctx} baseStyle={sizeStyle(ch.size)} />
              {i < node.children.length - 1 && <ResizeSplitter idA={ch.id} idB={node.children[i + 1].id} dir={node.dir} />}
            </React.Fragment>
          ))}
        </SortableContext>
      </div>
    </EditWrap>
  );
};

/** Mřížka (statická tabulka) v dnd módu: droppable + sortable buňky (rectSortingStrategy), grid-cell style. */
const DndGrid: React.FC<{ node: GridNode; ctx: WidgetContext }> = ({ node, ctx }) => {
  const { setNodeRef, isOver } = useDroppable({ id: "cont:" + node.id });
  const childIds = node.children.map((c) => c.node.id);
  return (
    <EditWrap id={node.id} kind="grid">
      <div
        ref={setNodeRef}
        className={cn(isOver && "bg-primary/10")}
        style={{ display: "grid", gridTemplateColumns: gridTemplate(node), rowGap: node.rowGap, columnGap: node.colGap, minHeight: 24 }}
      >
        <SortableContext items={childIds} strategy={rectSortingStrategy}>
          {node.children.map((c) => (
            <DndSlot
              key={c.node.id}
              node={c.node}
              ctx={ctx}
              baseStyle={{ gridColumn: `span ${Math.min(c.colSpan, node.cols)}`, minWidth: 0, ...(c.rowStart ? { gridRowStart: c.rowStart } : {}) }}
            />
          ))}
        </SortableContext>
      </div>
    </EditWrap>
  );
};

const NodeView: React.FC<{ node: LayoutNode; ctx: WidgetContext; edit?: boolean; dnd?: boolean }> = ({ node, ctx, edit, dnd }) => {
  const isMobile = useIsMobile();
  const wrap = (content: React.ReactNode) => (edit ? <EditWrap id={node.id} kind={node.kind}>{content}</EditWrap> : content);

  if (node.kind === "container") {
    // stackOnMobile: na < lg se ROW přepne na sloupec a děti dostanou plnou šířku.
    const stacked = !!node.stackOnMobile && node.dir === "row" && isMobile;
    if (dnd && !stacked) return <DndContainer node={node} ctx={ctx} />;
    return wrap(
      <div style={containerStyle(node, !!edit, stacked)}>
        {node.children.map((ch) => (
          <div key={ch.id} style={stacked ? STACKED_SLOT : sizeStyle(ch.size)}>
            <NodeView node={ch} ctx={ctx} edit={edit} dnd={dnd} />
          </div>
        ))}
      </div>
    );
  }

  if (node.kind === "grid") {
    if (dnd) return <DndGrid node={node} ctx={ctx} />;
    return wrap(
      <div style={{ display: "grid", gridTemplateColumns: gridTemplate(node), rowGap: node.rowGap, columnGap: node.colGap }}>
        {node.children.map((c) => (
          <div key={c.node.id} style={{ gridColumn: `span ${Math.min(c.colSpan, node.cols)}`, minWidth: 0, ...(c.rowStart ? { gridRowStart: c.rowStart } : {}) }}>
            <NodeView node={c.node} ctx={ctx} edit={edit} dnd={dnd} />
          </div>
        ))}
      </div>
    );
  }

  if (node.kind === "tabs") {
    return wrap(
      <Tabs defaultValue={node.tabs[0]?.id} className="w-full">
        <TabsList>
          {node.tabs.map((t) => (
            <TabsTrigger key={t.id} value={t.id}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
        {node.tabs.map((t) => (
          <TabsContent key={t.id} value={t.id}>
            <NodeView node={t.child} ctx={ctx} edit={edit} dnd={dnd} />
          </TabsContent>
        ))}
      </Tabs>
    );
  }

  // widget
  return wrap(<AppErrorBoundary>{renderWidget(node, ctx)}</AppErrorBoundary>);
};

export const LayoutNodeView = React.memo(NodeView);
export default LayoutNodeView;
